/**
 * ViewBox Calculation Utilities
 * Feature: 012-svg-zoom-pan
 * Package: @alchemiesolns/svg-container
 *
 * Provides utilities for:
 * - Parsing and formatting viewBox strings
 * - Converting screen coordinates to SVG coordinates
 * - Validating viewBox values
 */

import type { ViewBoxState } from '../types/useZoomPan.types';

const FALLBACK_VIEW_BOX: ViewBoxState = { x: 0, y: 0, width: 1, height: 1 };

/** True if values are finite and width/height are strictly positive. */
export function isValidViewBoxState(v: ViewBoxState): boolean {
  return (
    Number.isFinite(v.x) &&
    Number.isFinite(v.y) &&
    Number.isFinite(v.width) &&
    Number.isFinite(v.height) &&
    v.width > 0 &&
    v.height > 0
  );
}

/**
 * Format one viewBox number for the SVG `viewBox` attribute.
 * React's dev validator rejects scientific notation (e.g. `1e+21`), so we never emit `e`/`E`.
 */
export function formatViewBoxToken(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const s = n.toFixed(12);
  const trimmed = s.replace(/\.?0+$/, '');
  if (trimmed === '' || trimmed === '-') return '0';
  if (trimmed === '-0') return '0';
  return trimmed;
}

/**
 * Parse viewBox string into structured object
 *
 * Handles multiple formats:
 * - Space-separated: "0 0 500 500"
 * - Comma-separated: "0,0,500,500"
 * - Mixed: "0, 0, 500, 500"
 *
 * @param viewBoxString - SVG viewBox attribute value
 * @returns Parsed ViewBoxState object
 * @throws Error if string is invalid or contains non-numeric values
 *
 * @example
 * parseViewBox("0 0 500 500") // { x: 0, y: 0, width: 500, height: 500 }
 * parseViewBox("10, 20, 300, 400") // { x: 10, y: 20, width: 300, height: 400 }
 */
export function parseViewBox(viewBoxString: string): ViewBoxState {
  const parts = viewBoxString
    .trim()
    .split(/[\s,]+/) // Split on spaces and/or commas
    .filter(Boolean); // Remove empty strings

  if (parts.length !== 4) {
    throw new Error(
      `Invalid viewBox format: expected 4 values, got ${parts.length}. Input: "${viewBoxString}"`
    );
  }

  const [x, y, width, height] = parts.map(Number);

  // Validate all values are finite numbers
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
    throw new Error(
      `Invalid viewBox values: all values must be finite numbers. Input: "${viewBoxString}"`
    );
  }

  // Validate width and height are positive
  if (width <= 0 || height <= 0) {
    throw new Error(
      `Invalid viewBox dimensions: width and height must be positive. Got width=${width}, height=${height}`
    );
  }

  return { x, y, width, height };
}

/**
 * Parse a viewBox string or object; on invalid input returns a safe default (never throws).
 */
export function parseViewBoxSafe(input: string | ViewBoxState): ViewBoxState {
  if (typeof input === 'string') {
    try {
      const parsed = parseViewBox(input);
      return isValidViewBoxState(parsed) ? parsed : { ...FALLBACK_VIEW_BOX };
    } catch {
      return { ...FALLBACK_VIEW_BOX };
    }
  }
  return isValidViewBoxState(input) ? input : { ...FALLBACK_VIEW_BOX };
}

/**
 * Format ViewBoxState object into SVG viewBox attribute string
 *
 * Produces space-separated format: "x y width height"
 *
 * @param viewBox - ViewBox state object
 * @returns Formatted viewBox string
 *
 * @example
 * viewBoxToString({ x: 0, y: 0, width: 500, height: 500 }) // "0 0 500 500"
 * viewBoxToString({ x: 10.5, y: 20.5, width: 300, height: 400 }) // "10.5 20.5 300 400"
 */
export function viewBoxToString(viewBox: ViewBoxState): string {
  const v = isValidViewBoxState(viewBox) ? viewBox : { ...FALLBACK_VIEW_BOX };
  return `${formatViewBoxToken(v.x)} ${formatViewBoxToken(v.y)} ${formatViewBoxToken(v.width)} ${formatViewBoxToken(v.height)}`;
}

/**
 * Type guard to validate viewBox string format
 *
 * Checks that:
 * - String contains exactly 4 numeric values
 * - All values are finite numbers (not NaN or Infinity)
 * - Width and height are positive
 *
 * @param str - String to validate
 * @returns True if string is valid viewBox format
 *
 * @example
 * isValidViewBox("0 0 500 500") // true
 * isValidViewBox("0 0 -500 500") // false (negative width)
 * isValidViewBox("0 0 500") // false (only 3 values)
 * isValidViewBox("0 0 NaN 500") // false (NaN value)
 */
export function isValidViewBox(str: string): boolean {
  try {
    const parts = str
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean);

    if (parts.length !== 4) {
      return false;
    }

    const [x, y, width, height] = parts.map(Number);

    // Check all values are finite numbers
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
      return false;
    }

    // Check width and height are positive
    if (width <= 0 || height <= 0) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Convert screen pixel coordinates to SVG coordinate space
 *
 * Uses native browser APIs (getScreenCTM) for accurate transformation
 * that accounts for:
 * - ViewBox transformations
 * - SVG element positioning
 * - Nested transformations
 * - PreserveAspectRatio settings
 *
 * Implementation based on Decision 2 from research.md:
 * - Uses getScreenCTM().inverse() for mathematically correct conversion
 * - DOMPointReadOnly for browser-native matrix operations
 *
 * @param svgElement - SVG element (must be mounted in DOM)
 * @param screenX - Screen X coordinate (clientX from event)
 * @param screenY - Screen Y coordinate (clientY from event)
 * @returns SVG coordinate space position
 * @throws Error if SVG element is not mounted or getScreenCTM returns null
 *
 * @example
 * // Mouse event in SVG
 * const handleMouseMove = (e: MouseEvent) => {
 *   const svgPos = screenToSVG(svgRef.current, e.clientX, e.clientY);
 *   console.log(`SVG coordinates: ${svgPos.x}, ${svgPos.y}`);
 * };
 */
export function screenToSVG(
  svgElement: SVGSVGElement,
  screenX: number,
  screenY: number
): { x: number; y: number } {
  // Get screen transformation matrix
  const ctm = svgElement.getScreenCTM();

  if (!ctm) {
    throw new Error(
      'Cannot get screen CTM: SVG element may not be mounted in the DOM'
    );
  }

  // Get inverse matrix
  const inverse = ctm.inverse();

  // Manual matrix transformation: [x', y', 1] = [x, y, 1] * inverse
  // For 2D affine transform: x' = a*x + c*y + e, y' = b*x + d*y + f
  const svgX = inverse.a * screenX + inverse.c * screenY + inverse.e;
  const svgY = inverse.b * screenX + inverse.d * screenY + inverse.f;

  return {
    x: svgX,
    y: svgY,
  };
}

/**
 * Convert screen pixel delta to SVG coordinate delta
 *
 * Useful for pan operations where you need to convert mouse/touch movement
 * from screen pixels to SVG coordinates.
 *
 * Simple calculation based on viewBox ratio:
 * - scaleX = viewBox.width / svgElement.clientWidth
 * - scaleY = viewBox.height / svgElement.clientHeight
 *
 * @param svgElement - SVG element (must be mounted in DOM)
 * @param viewBox - Current viewBox state
 * @param screenDeltaX - Screen X delta in pixels
 * @param screenDeltaY - Screen Y delta in pixels
 * @returns SVG coordinate space delta
 *
 * @example
 * // Pan operation
 * const handleMouseMove = (e: MouseEvent) => {
 *   const deltaX = e.clientX - lastX;
 *   const deltaY = e.clientY - lastY;
 *   const svgDelta = screenDeltaToSVGDelta(svgRef.current, viewBox, deltaX, deltaY);
 *   dispatch({ type: 'PAN', deltaX: svgDelta.dx, deltaY: svgDelta.dy });
 * };
 */
export function screenDeltaToSVGDelta(
  svgElement: SVGSVGElement,
  viewBox: ViewBoxState,
  screenDeltaX: number,
  screenDeltaY: number
): { dx: number; dy: number } {
  const scaleX = viewBox.width / svgElement.clientWidth;
  const scaleY = viewBox.height / svgElement.clientHeight;

  return {
    dx: screenDeltaX * scaleX,
    dy: screenDeltaY * scaleY,
  };
}
